// ARCA-769 (ARCA-717 R2) — client mirror of the backend chess move geometry,
// parameterized by an explicit ROLE so the composite-turn UI can recompute a
// staged move's legality under a freshly-STAGED empower role.
//
// Why a client mirror exists at all (DEC-021 keeps movement rules
// server-authoritative via PlayerView.your_legal_moves): that map reflects the
// PRE-empower board, so once the player stages an empower it can no longer tell
// whether a same-piece move is legal under the NEW role. The canonical apply
// order is powerup → empower → move (apps/backend/src/match/validation/chess.ts
// `normalizeChessStep`, D3), so the server validates the move against the
// post-empower role and 409s `phase_b:illegal_move` on a mismatch. This module
// reproduces EXACTLY the backend `isLegalMove`/`pathClear` geometry
// (apps/backend/src/match/validation/chess.ts `isLegalMove` ~ln 354,
// `pathClear` ~ln 395, `isMoveLegal` ~ln 200) so the client can catch that
// combination and block Submit before it round-trips.
//
// Scope: geometry only, under a caller-supplied role. It does NOT simulate
// power-up board mutations (teleport/freeze) — the composite conflict guard
// only fires for a staged empower+move on the SAME piece, where occupancy is
// unchanged by the empower. Pure; unit-tested in chess-move-geometry.test.ts.

import { BOARD_COLS, CHESS_CELLS } from './layers/board-layout'
import type { PlayerView, RoleKind } from './PlayerViewSchema'

type Board = PlayerView['board']

/** Cell-index → row/col on the fixed 6-wide chess grid. Mirrors the backend
 *  `idxToRC` (apps/backend/src/match/validation/path.ts), which divides by the
 *  invariant column count. */
function idxToRC(idx: number): { r: number; c: number } {
  return { r: Math.floor(idx / BOARD_COLS), c: idx % BOARD_COLS }
}

/** True iff every cell strictly between `from` and `to` is empty. Mirrors the
 *  backend `pathClear` — destination occupancy is the caller's concern. */
function pathClear(board: Board, from: number, to: number): boolean {
  const A = idxToRC(from)
  const B = idxToRC(to)
  const dr = Math.sign(B.r - A.r)
  const dc = Math.sign(B.c - A.c)
  let r = A.r + dr
  let c = A.c + dc
  while (r !== B.r || c !== B.c) {
    const idx = r * BOARD_COLS + c
    const cell = board.find((cc) => cc.cell_idx === idx)
    if (cell && cell.piece_owner_slot !== null) return false
    r += dr
    c += dc
  }
  return true
}

/** Geometry predicate for a piece of `role` moving from→to. Mirrors the backend
 *  `isLegalMove` switch (king/knight/bishop/rook/queen) incl. the `pathClear`
 *  gate on the sliding roles. Pure over the board occupancy. */
export function isLegalMoveForRole(
  role: RoleKind,
  from: number,
  to: number,
  board: Board,
): boolean {
  const A = idxToRC(from)
  const B = idxToRC(to)
  const adr = Math.abs(B.r - A.r)
  const adc = Math.abs(B.c - A.c)
  switch (role) {
    case 'king':
      return adr <= 1 && adc <= 1 && adr + adc > 0
    case 'knight':
      return (adr === 2 && adc === 1) || (adr === 1 && adc === 2)
    case 'bishop':
      return adr === adc && adr > 0 && pathClear(board, from, to)
    case 'rook':
      return (
        (adr === 0 || adc === 0) && adr + adc > 0 && pathClear(board, from, to)
      )
    case 'queen':
      return (
        ((adr === adc && adr > 0) ||
          ((adr === 0 || adc === 0) && adr + adc > 0)) &&
        pathClear(board, from, to)
      )
    default:
      return false
  }
}

/** Whether the piece at `from` (owned by `slot`) may legally move to `to` when
 *  its role is OVERRIDDEN to `role` — the staged-empower recompute. Mirrors the
 *  backend `isMoveLegal` envelope (6×6 bounds, ownership, no own-capture) but
 *  substitutes the staged role for the board's current `role_kind`. An empowered
 *  role is never blank/null, so the blank-piece arm of the backend predicate
 *  (1-square orthogonal, no capture) is intentionally not reproduced here. */
export function isMoveLegalUnderRole(
  board: Board,
  slot: 0 | 1,
  from: number,
  to: number,
  role: RoleKind,
): boolean {
  if (from === to) return false
  // ARCA-749 — the chess grid is a fixed 6×6; a piece can never move onto/from a
  // word-only cell (cell_idx ≥ CHESS_CELLS) no matter how deep the word field is.
  if (from < 0 || from >= CHESS_CELLS || to < 0 || to >= CHESS_CELLS)
    return false
  const fromCell = board.find((c) => c.cell_idx === from)
  const toCell = board.find((c) => c.cell_idx === to)
  if (!fromCell || !toCell) return false
  if (fromCell.piece_owner_slot !== slot) return false
  // Empowered piece: cannot capture own; geometry via the role switch.
  if (toCell.piece_owner_slot === slot) return false
  return isLegalMoveForRole(role, from, to, board)
}
